/**
 * inflight.js — share an identical request that is ALREADY in flight.
 *
 * Independent components fetch the same thing at the same time, because each
 * one is honestly responsible for its own data. On /discovery/org that was
 * measured as 50 API calls per page load, of which several were exact
 * duplicates issued within milliseconds of each other:
 *
 *   /org-structure/allocations/summary?tier_level=0&unit_id=__org__   x2  (11.0s, 10.2s)
 *   /org-structure/child-emails?tier_level=0                          x2  (14.5s,  8.0s)
 *   /discovery/billing-by-child-unit?days=365&…                       x2  (17.7s, 16.4s)
 *
 * One uvicorn worker serves the API, so a duplicate is not merely wasted —
 * it takes a slot the page's OTHER calls are queued behind, which is how a
 * 0 KB response ended up taking 11 seconds.
 *
 * This SHARES an in-flight promise and clears it the moment it settles. It is
 * deliberately NOT a cache: a later mount, or a refetch after data changes,
 * still goes to the server, so this can never serve a stale figure — the one
 * thing a money surface must not do. (`useBudgetData` has carried a local copy
 * of this for its own three fetches; it now imports this one so there is a
 * single implementation.)
 *
 * Callers of a coalesced GET must treat the result as READ-ONLY: two of them
 * may hold the same object.
 *
 * ── Read-after-write ────────────────────────────────────────────────────────
 * "Not a cache" is not enough on its own. A refetch issued AFTER a write can
 * still land on a read that started BEFORE it, and then it answers with
 * pre-write data — which is a stale figure by a different route.
 *
 * That is not hypothetical: the Budget Alerts dialog lists its rules (a slow
 * read on a seeded org — it prices every rule), and saving a new rule reloads
 * that list to select what was just created. The reload joined the list request
 * still in flight from the moment the dialog opened, the new rule was
 * therefore absent, the selection fell back to the first EXISTING rule — and
 * the next Save, and "Send test", silently operated on somebody else's alert.
 *
 * So writes bump an epoch, and the epoch is part of the key: a read issued
 * after a write can never be handed the answer to a read that began before it.
 * Reads issued after that same write still share with each other.
 */
const _inFlight = new Map();
const _recent = new Map();
let _epoch = 0;

/**
 * Invalidate in-flight read sharing. Call this when a WRITE is issued — the
 * service modules do it for every non-GET request, so call sites don't have to
 * remember.
 */
export function bumpInFlightEpoch() {
  _epoch += 1;
  _recent.clear();
  // Entries from an earlier epoch can no longer be joined (their keys are
  // unreachable), so drop them: whoever is awaiting them still gets their
  // answer, this only stops the map growing.
  _inFlight.clear();
}

/**
 * @param {string} key   identifies the request — use the full URL/query, or the
 *                       result of one caller will be handed to another asking a
 *                       different question.
 * @param {() => Promise<any>} run  performs the request.
 */
export function coalesceInFlight(key, run) {
  const scoped = `${_epoch} ${key}`;
  const hit = _inFlight.get(scoped);
  if (hit) {return hit;}
  const p = run().finally(() => { _inFlight.delete(scoped); });
  _inFlight.set(scoped, p);
  return p;
}

/** Test seam: how many requests are currently shared. */
export function inFlightCount() {
  return _inFlight.size;
}

/**
 * `fetch` for a service module: bumps the epoch on any non-GET so a write
 * invalidates read sharing, then delegates. Shadowing the global in one place
 * per module is what keeps every write site honest without editing each one.
 */
export function trackedFetch(input, init) {
  const method = (init && init.method ? String(init.method) : 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {bumpInFlightEpoch();}
  // Resolved at CALL time, off `window` — so a test that installs its own
  // fetch is still the one that runs. (`window`, not `globalThis`: the CRA
  // eslint env does not declare the latter, and an eslint error here is a
  // FAILED COMPILE whose dev-server overlay then eats every click on the login
  // page — 18 e2e failures in surfaces that have nothing to do with this file.)
  return window.fetch(input, init);
}

/**
 * Like coalesceInFlight, and the answer is also kept for `ttlMs` after it
 * settles, so a card that mounts again (switching tabs, the layout editor)
 * does not ask again. Only for slow reads that do not carry money and change
 * rarely — who is autonomous, which unit funds a person. Any write from this
 * console (the epoch) drops every kept answer, so a read after a write still
 * goes to the server. A failed read is not kept.
 */
export function reuseRecent(key, ttlMs, run) {
  const scoped = `${_epoch} ${key}`;
  const kept = _recent.get(scoped);
  if (kept && kept.until > Date.now()) {return kept.promise;}
  const promise = coalesceInFlight(key, run);
  _recent.set(scoped, { promise, until: Date.now() + ttlMs });
  promise.catch(() => { if (_recent.get(scoped)?.promise === promise) {_recent.delete(scoped);} });
  return promise;
}
